import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import googleLogo from "../../../assets/images/google.svg";
import { updateUser } from "../../../store/users/userReducer";
import { IUser } from "../../../types";
import { getGoogleAccounts, getGoogleLocations } from "../../../store/googleBusiness/googleBusinessReducer";
import Spinner from "../../../components/Spinner/Index";

const OnboardingWizard: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
    const [selectedBusinesses, setSelectedBusinesses] = useState<string[]>([]);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { accounts, locations, isLoading } = useSelector((state: RootState) => state.googleBusiness);
    const { token, user } = useSelector((state: RootState) => state.auth) as { token: string, user: IUser };

    useEffect(() => {
        if (user?.isNewUser === false) {
            navigate("/user/businesses/create");
        }
    }, [user, navigate]);

    // Handle Business Type Selection
    const handleSelection = async (type: "import" | "manual") => {
        if (type === "manual") {
            try {
                await dispatch(updateUser({
                    userData: { isNewUser: false, wizardCompletedAt: new Date().toISOString() },
                    token
                }) as any);
                navigate("/user/businesses/create");
            } catch (error) {
                console.error("❌ Error updating user:", error);
            }
        }

        if (type === "import") {
            console.log("📢 Fetching Google Business Profile accounts...");
            dispatch(getGoogleAccounts(token) as any);
            setCurrentStep(1);
        }
    };

    // Handle Account Selection and Fetch Locations
    const handleAccountSelection = async () => {
        if (!selectedAccount) return;
        await dispatch(getGoogleLocations(selectedAccount, token) as any);
        setCurrentStep(2);
    };

    const handleImportBusinesses = async () => {
        if (selectedBusinesses.length === 0) {
            setCurrentStep(1);
        }
        await dispatch(updateUser({
            userData: { isNewUser: false, wizardCompletedAt: new Date().toISOString() },
            token
        }) as any);
        navigate("/user/businesses");
    };

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 sm:px-8 lg:px-16">
            <div className="max-w-3xl w-full bg-white p-10 rounded-lg shadow-lg">

                {/* Step 1: Choose Business Type */}
                {currentStep === 0 && (
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                            How would you like to add your business?
                        </h2>
                        <div className="space-y-6">
                            <button
                                onClick={() => handleSelection("import")}
                                className="w-full flex items-center justify-center px-5 py-4 border rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-100 transition"
                            >
                                <img src={googleLogo} alt="Google" className="h-7 w-7 mr-3" />
                                <span className="text-lg">Import from Google Business Profile</span>
                            </button>
                            <button
                                onClick={() => handleSelection("manual")}
                                className="w-full px-5 py-4 border rounded-lg shadow-sm bg-green-600 text-white hover:bg-green-500 transition text-lg"
                            >
                                Add Manually
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Select Google Business Profile Account */}
                {currentStep === 1 && (
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Select Your Google Account</h2>

                        {isLoading ? (
                            <p className="text-center text-gray-500">Fetching your accounts...</p>
                        ) : (
                            <div className="space-y-4">
                                {accounts.length > 0 ? (
                                    accounts.map((acc) => (
                                        <button
                                            key={acc.name} // Using "name" as the key
                                            className={`w-full px-5 py-3 border rounded-lg text-lg ${selectedAccount === acc.name ? "bg-green-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"
                                                }`}
                                            onClick={() => setSelectedAccount(acc.name)} // Now using "name"
                                        >
                                            {acc.accountName}
                                        </button>
                                    ))
                                ) : (
                                    <p className="text-center text-gray-500">No accounts found.</p>
                                )}
                            </div>
                        )}

                        <div className="flex justify-between mt-8">
                            <button
                                onClick={() => setCurrentStep(0)}
                                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleAccountSelection}
                                className="px-6 py-3 bg-green-600 text-white rounded-lg"
                                disabled={!selectedAccount}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Display Locations under Selected Account */}
                {currentStep === 2 && (
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">We're importing the following businesses</h2>

                        {isLoading ? (
                            <p className="text-center text-gray-500">Fetching locations...</p>
                        ) : (
                            <div className="space-y-4">
                                {locations.length > 0 ? (
                                    locations.map((location) => (
                                        <button
                                            key={location.id}
                                            className="w-full px-5 py-3 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 text-lg"
                                        >
                                            {location.businessInformation.businessName}
                                        </button>
                                    ))
                                ) : (
                                    <p className="text-center text-gray-500">No locations found.</p>
                                )}
                            </div>
                        )}

                        <div className="flex justify-between mt-8">
                            <button
                                onClick={() => setCurrentStep(1)}
                                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleImportBusinesses}
                                className="px-6 py-3 bg-green-600 text-white rounded-lg"
                            >
                                Import Businesses
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OnboardingWizard;
